import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  compararQuantidade,
  desescalar,
  dividirArredondando,
  escalar,
  multiplicarQuantidadePorValor,
  somarDinheiro,
  somarQuantidade,
  subtrairQuantidade,
} from './aritmetica.ts';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  precoUnitarioSchema,
  quantidadeSchema,
  respostaListaSchema,
  situacaoAprovacaoSchema,
  situacaoDocumentoSchema,
  tipoInsumoSchema,
  uuidSchema,
} from './comum.ts';
import { freteCotacaoSchema, freteTemValor, type FreteCotacao } from './cotacoes-preco.ts';
import {
  impostosDestacadosEntradaSchema,
  impostosDestacadosSchema,
  type ImpostosDestacados,
  type ImpostosDestacadosEntrada,
} from './impostos.ts';

/**
 * Pedido de compra (`.spec/010`, "Pedido de Compra"; `.spec/016`, seção 7).
 *
 * É o **compromisso com o fornecedor**: a solicitação diz o que a obra precisa,
 * a cotação diz quanto custa, e o pedido é o documento que fecha a compra. Vem
 * depois dos dois e aponta para os dois — `solicitacaoCompraItemId` no item diz
 * qual saldo ele consome, e `cotacaoPropostaId` diz de qual oferta o preço veio.
 *
 * Três coisas o distinguem da solicitação, que serviu de molde:
 *
 * - **Tem valor, e o valor é fato daquela data.** `valorInsumo`, `valorTotal` e
 *   `valorCustoEstoque` são gravados, não recalculados na leitura (ADR 0004):
 *   recalcular faria o total de março mudar junto com qualquer correção futura
 *   da fórmula. Quem faz a conta é `valoresDoItemDePedido`, aqui, que é a única
 *   implementação — a tela mostra o número que o servidor grava.
 * - **Tem imposto e rateio.** Os dois são filhos do **item**, e não do
 *   documento: cada item pode ter tributação própria, e o rateio distribui
 *   aquele material entre obras. Um imposto por documento obrigaria a repartir o
 *   ICMS entre os itens na hora de compor o custo do estoque, que é justamente
 *   quando ele precisa estar por item.
 * - **Escreve em dois outros documentos.** Ao gravar, ele soma
 *   `quantidade_atendida` no item da solicitação e `quantidade_pedido` na
 *   proposta da cotação. É por isso que o pedido em si tem trava: alterá-lo
 *   exige desfazer o que ele escreveu lá.
 *
 * O que ele **não** faz é movimentar estoque. Quem movimenta é a nota de compra,
 * e é lá que o crédito de ICMS, PIS e COFINS entra na conta do custo — ver
 * `valorCustoEstoque` abaixo.
 */

/* -------------------------------------------------------------------------- */
/* Códigos e rótulos                                                          */
/* -------------------------------------------------------------------------- */

export const SITUACOES_PEDIDO_COMPRA = situacaoDocumentoSchema.options;
export type SituacaoPedidoCompra = z.infer<typeof situacaoDocumentoSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_SITUACAO_PEDIDO: Readonly<Record<SituacaoPedidoCompra, string>> = {
  PENDENTE: 'Pendente',
  ATENDIDO: 'Atendido',
  CANCELADO: 'Cancelado',
  BAIXADO: 'Baixado',
};

/**
 * O que cada situação significa, para a dica da tela.
 *
 * São as mesmas quatro da solicitação (`.spec/010`, "Situações da Solicitação e
 * do Pedido de Compra"), e o texto muda porque o que atende é outro: a
 * solicitação é atendida por pedido, e o pedido é atendido por **nota de
 * compra** — a mercadoria que chegou.
 */
export const DICA_SITUACAO_PEDIDO: Readonly<Record<SituacaoPedidoCompra, string>> = {
  PENDENTE: 'Nada entregue, ou entregue em parte. É a única situação que se altera.',
  ATENDIDO: 'Todos os itens foram cobertos por nota de compra. Situação automática.',
  CANCELADO: 'Anulado sem nenhuma entrega — o pedido não deveria ter existido.',
  BAIXADO: 'Encerrado com saldo em aberto que o fornecedor não vai mais entregar.',
};

/**
 * Quem paga o frete — os **mesmos três códigos da cotação**, de propósito.
 *
 * O schema é reaproveitado em vez de redeclarado: o frete que o fornecedor
 * propôs na cotação é o frete que o pedido fecha, e dois enums com os mesmos
 * três valores acabariam divergindo no dia em que alguém acrescentasse um
 * quarto de um lado só.
 *
 * Diferente da cotação, aqui ele mora no **cabeçalho**: o pedido é de um
 * fornecedor só, com uma condição só, e o transporte é do carregamento inteiro.
 * O valor cobrado acompanha a modalidade e mora no cabeçalho pelo mesmo motivo —
 * ver `valorFrete` em `criarPedidoCompraSchema`.
 */
export const fretePedidoSchema = freteCotacaoSchema;
export type FretePedido = FreteCotacao;

/**
 * Teto de itens por pedido.
 *
 * Não é julgamento sobre o negócio: existe para que um `curl` com dez mil linhas
 * não vire dez mil `INSERT` numa transação. O mesmo teto da solicitação, porque
 * um pedido nasce de uma ou de algumas delas.
 */
export const MAXIMO_ITENS_PEDIDO = 200;

/**
 * Teto de rateios por item.
 *
 * Um material dividido entre mais de vinte obras não é rateio, é compra
 * centralizada — e essa se registra sem rateio, deixando o consumo dizer para
 * onde o material foi.
 */
export const MAXIMO_RATEIOS_ITEM = 20;

/* -------------------------------------------------------------------------- */
/* Impostos do item                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A tributação do item — **o mesmo desenho da cotação** (`impostos.ts`).
 *
 * Eram onze valores soltos, sem base nem alíquota, e a diferença em relação à
 * proposta obrigava a geração de pedidos a traduzir entre os dois formatos: PIS
 * e COFINS iam zerados porque a cotação não os tem, e as três retenções federais
 * eram somadas num campo só. A tradução carregava um comentário dizendo que era
 * temporária. Este alias é o fim dela.
 *
 * O que a mudança compra não é uniformidade, é a **conta do atendimento
 * parcial**: com base e alíquota gravadas nos dois documentos, a proposta de 100
 * sacos que vira pedido de 60 tem o imposto refeito sobre a base nova, e não
 * uma regra de três sobre um valor já arredondado. Ver `impostosProporcionais`.
 *
 * Três diferenças em relação ao que havia aqui, e as três vêm da cotação:
 *
 * - **PIS e COFINS por dentro saíram.** O crédito deles não depende de quem
 *   vende, e sim do regime de quem compra — é conta da nota, e as colunas do
 *   pedido nunca chegaram a ser preenchidas por caminho nenhum.
 * - **As três retenções federais se separaram.** "3.650,00 de retenções" não
 *   diz o que recolher em cada guia; PIS, COFINS e CSLL separados dizem.
 * - **Entrou o crédito do Simples Nacional**, que a proposta registra e o pedido
 *   não tinha onde guardar.
 *
 * **Qual conjunto vale é decidido por item**, e é a diferença em relação à
 * cotação: ela tem um insumo só e decide pelo documento; o mesmo pedido compra
 * cimento e contrata a bomba de concretagem. Quem decide é `blocosDeImposto`,
 * sobre o `insumoTipo` que a leitura devolve em cada item.
 */
export const impostosDoItemEntradaSchema = impostosDestacadosEntradaSchema;
export type ImpostosDoItemEntrada = ImpostosDestacadosEntrada;

export const impostosDoItemSchema = impostosDestacadosSchema;
export type ImpostosDoItem = ImpostosDestacados;

/* -------------------------------------------------------------------------- */
/* Rateio de obra                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Uma parcela do item, destinada a uma obra (`.spec/010`, "Rateio Obra").
 *
 * O rateio do pedido é **por quantidade**, e não por percentual como o do
 * financeiro. A diferença vem do que se está dividindo: aqui são sacos de
 * cimento, e sacos não se partem em 33,33%. Dividir por percentual obrigaria a
 * distribuir a sobra saco a saco, que é a mecânica que existe para dinheiro e
 * não para material.
 *
 * A obra é **opcional** pela mesma razão do cabeçalho da solicitação: parte da
 * compra pode ser de almoxarifado central, sem obra nenhuma. O depósito é
 * obrigatório porque a parcela tem sempre um destino físico.
 */
export const rateioDoItemEntradaSchema = z.object({
  obraId: uuidSchema.nullable().prefault(null),
  /** Etapa da obra. Só pode vir com a obra — a API recusa a parte sozinha. */
  obraParteId: uuidSchema.nullable().prefault(null),
  depositoId: uuidSchema,
  quantidade: quantidadeSchema.refine(
    (v) => Number(v) > 0,
    'A quantidade do rateio deve ser maior que zero.',
  ),
});

export type RateioDoItemEntrada = z.infer<typeof rateioDoItemEntradaSchema>;

/** Rateio como a API o devolve, com os nomes resolvidos. */
export const rateioDoItemSchema = z.object({
  id: uuidSchema,
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  obraParteId: uuidSchema.nullable(),
  obraParteNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  quantidade: quantidadeSchema,
});

export type RateioDoItem = z.infer<typeof rateioDoItemSchema>;

/* -------------------------------------------------------------------------- */
/* Aritmética do item                                                         */
/* -------------------------------------------------------------------------- */

/** O que o item custa, depois das contas. Strings decimais, como tudo. */
export interface ValoresDoItemDePedido {
  /** `quantidadePedida × valorUnitario`, arredondado a centavo. */
  valorInsumo: string;
  /**
   * `valorInsumo + valorOutros + valorFrete − valorDesconto`. É o que se deve
   * ao fornecedor por esta linha.
   *
   * **O frete entra aqui**, e a mudança tem duas razões, uma de cada ponta da
   * cadeia. Antes ele era um valor único no cabeçalho do pedido, somado ao total
   * do documento e a nenhum item.
   *
   * - **Atrás**, a proposta da cotação já cobra frete por linha, e a geração de
   *   pedidos precisava somar o frete de várias propostas num campo só do
   *   cabeçalho — perdendo de qual material era cada parte.
   * - **À frente**, a nota de compra vai levar o frete por item, ao lado de
   *   "outros" e do desconto. É o que faz o custo de cada material sair pronto
   *   da linha, sem depender de um rateio do cabeçalho para os itens.
   *
   * A modalidade continua no cabeçalho: quem paga o transporte é do documento,
   * porque o pedido é de um fornecedor só e uma negociação só.
   */
  valorTotal: string;
  /**
   * O que vai entrar no estoque quando a mercadoria chegar.
   *
   * `valorTotal + IPI + ICMS ST`, **sem descontar crédito nenhum**, e a ausência
   * é deliberada. `.spec/010` define o custo como "valor total + IPI + ICMS ST −
   * créditos de ICMS/PIS/COFINS", mas o crédito depende de duas coisas que o
   * pedido não conhece: a **operação fiscal** do item, que só aparece na nota de
   * compra, e o regime tributário da empresa aplicado item a item. Descontar
   * aqui um crédito que talvez não exista produziria um custo previsto menor que
   * o real em toda compra de empresa do Simples.
   *
   * Por isso este número é **previsão**, e o custo que entra no kardex é o da
   * nota. O IPI e o ICMS ST entram porque são cobrados por fora — somam ao que
   * se paga, independentemente de crédito.
   */
  valorCustoEstoque: string;
}

/**
 * Os valores derivados do item, calculados **aqui**.
 *
 * Vive no contrato, e não no service, pelo mesmo motivo de `valoresDaProposta`:
 * a tela mostra o total enquanto o comprador digita e o servidor grava o número
 * que vale. Duas implementações discordariam em algum arredondamento, e a
 * divergência apareceria como um total que muda ao salvar.
 */
export function valoresDoItemDePedido(item: {
  quantidadePedida: string;
  valorUnitario: string;
  valorOutros: string;
  /** Só existe no frete `CIF` do cabeçalho. Zero nas outras modalidades. */
  valorFrete?: string | undefined;
  valorDesconto: string;
  impostos: { valorIpi: string; valorIcmsSt: string };
}): ValoresDoItemDePedido {
  const insumo = multiplicarQuantidadePorValor(item.quantidadePedida, item.valorUnitario);

  const total =
    insumo +
    escalar(item.valorOutros, CASAS_DINHEIRO) +
    escalar(item.valorFrete ?? '0', CASAS_DINHEIRO) -
    escalar(item.valorDesconto, CASAS_DINHEIRO);

  const custo =
    total +
    escalar(item.impostos.valorIpi, CASAS_DINHEIRO) +
    escalar(item.impostos.valorIcmsSt, CASAS_DINHEIRO);

  return {
    valorInsumo: desescalar(insumo, CASAS_DINHEIRO),
    valorTotal: desescalar(total, CASAS_DINHEIRO),
    valorCustoEstoque: desescalar(custo, CASAS_DINHEIRO),
  };
}

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

const quantidadePedidaSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade pedida deve ser maior que zero.',
);

/**
 * Valor que não pode ser negativo — o unitário, o de outros e o desconto.
 *
 * Desconto negativo seria acréscimo escondido, e acréscimo tem campo próprio
 * ("outros"). Sem esta recusa, o mesmo item poderia ser digitado de duas formas
 * que somam igual, e o relatório que separa desconto de despesa acessória
 * passaria a medir a preferência de quem digitou.
 */
const valorNaoNegativoSchema = dinheiroSchema.refine(
  (v) => Number(v) >= 0,
  'O valor não pode ser negativo.',
);

/**
 * O **preço unitário**, que tem seis casas e não duas.
 *
 * Separado do acima porque a escala é outra: o unitário é preço por unidade e
 * aceita `6.123456` — o litro de diesel —, enquanto "outros", frete e desconto
 * são dinheiro do documento e continuam em centavos. Ver `precoUnitarioSchema`.
 */
const precoNaoNegativoSchema = precoUnitarioSchema.refine(
  (v) => Number(v) >= 0,
  'O preço não pode ser negativo.',
);

const rateiosSchema = z
  .array(rateioDoItemEntradaSchema)
  .max(MAXIMO_RATEIOS_ITEM, `O item tem no máximo ${MAXIMO_RATEIOS_ITEM} rateios.`)
  .prefault([]);

/**
 * Item no payload de gravação.
 *
 * **Não tem `valorInsumo`, `valorTotal` nem `valorCustoEstoque`.** Os três são
 * conta sobre os outros campos, e aceitá-los deixaria o cliente gravar um total
 * que não bate com o que ele mesmo mandou.
 *
 * **Não tem `quantidadeAtendida` nem `quantidadeBaixada`.** Quem escreve a
 * primeira é a nota de compra, ao receber a mercadoria; a segunda cai por
 * consequência do encerramento. É a mesma decisão do item da solicitação, e pelo
 * mesmo motivo: o documento não pode se declarar atendido sem que nada o tenha
 * atendido.
 *
 * **Não tem `id`.** Os itens são regravados por inteiro a cada `PUT`, como as
 * parcelas da condição de pagamento e os itens da solicitação.
 */
export const itemPedidoCompraEntradaSchema = z.object({
  /**
   * O insumo, e **não** a linha de `insumos_empresa` que o banco guarda.
   *
   * Mesma escolha do item da solicitação e do cabeçalho da cotação: pedir o
   * vínculo ao cliente obrigaria a tela a abrir o cadastro de cada insumo para
   * descobrir o id, e o primeiro que errasse o par compraria material de outra
   * empresa sem que nada acusasse.
   */
  insumoId: uuidSchema,
  /** Para onde este material vai. Por item: um pedido serve dois locais. */
  depositoId: uuidSchema,
  /**
   * O saldo de solicitação que este item consome. Opcional.
   *
   * Nulo é compra **sem solicitação prévia** (`.spec/016`, seção 7) — reposição
   * de almoxarifado, urgência do canteiro resolvida por telefone. Preenchido, é
   * o que faz o pedido escrever `quantidade_atendida` lá e o que sujeita a
   * quantidade à política de excedente da conta.
   */
  solicitacaoCompraItemId: uuidSchema.nullable().prefault(null),
  /**
   * A proposta de cotação de onde o preço veio. Opcional.
   *
   * Aponta para a **proposta**, e não para a cotação, ainda que `.spec/010`
   * escreva "ID Cotação". A proposta identifica a cotação (é filha dela) e diz
   * mais: qual fornecedor, em qual condição, por qual preço. Sem ela, o pedido
   * não teria onde somar `quantidade_pedido`, que é justamente o número que
   * trava a alteração da cotação — e o vínculo com a cotação viraria decorativo.
   */
  cotacaoPropostaId: uuidSchema.nullable().prefault(null),
  /**
   * O item de **lista de compra direta** que gerou esta linha. Opcional.
   *
   * A terceira origem (`.spec/015`, decisão 46), e a mais simples das três: a
   * lista não tem saldo a consumir nem quantidade a somar de volta — ela vira
   * pedido de uma vez. O vínculo existe para o outro lado: é por ele que a
   * situação da lista é recalculada, e é o que faz o pedido excluído devolver a
   * lista a pendente.
   *
   * **Escrito só pela geração.** Nada impede tecnicamente que um `POST` de
   * pedido o traga, e a validação de origem distinta o cobre; o que não existe é
   * tela que o preencha — o pedido digitado à mão não sabe de lista nenhuma.
   */
  listaCompraDiretaItemId: uuidSchema.nullable().prefault(null),
  quantidadePedida: quantidadePedidaSchema,
  /** Preço de uma unidade — **seis casas**, por causa do combustível. */
  valorUnitario: precoNaoNegativoSchema,
  /** Despesas acessórias que o fornecedor cobra à parte — embalagem, montagem. */
  valorOutros: valorNaoNegativoSchema.prefault('0'),
  /**
   * O que o fornecedor cobra pelo transporte **desta linha**. Só no frete `CIF`.
   *
   * Por item, e não no cabeçalho como era: é assim que a proposta da cotação já
   * o registra, e é o que permite a nota de compra levar o frete ao lado de
   * "outros" e do desconto em cada linha. O custo de cada material sai pronto do
   * item, sem um rateio do cabeçalho no meio.
   *
   * Campo próprio, e não uma parcela de "outros", porque é o que se negocia
   * separado: "entrego de graça acima de dez mil" é conversa de frete, e diluí-lo
   * em despesas acessórias apagaria a informação que o comprador confere contra
   * o documento do fornecedor.
   *
   * Zero fora do `CIF`, e quem recusa é `validarFreteDosItens`: a modalidade mora
   * no cabeçalho, então o `CHECK` do banco não alcança o par a partir daqui.
   */
  valorFrete: valorNaoNegativoSchema.prefault('0'),
  valorDesconto: valorNaoNegativoSchema.prefault('0'),
  impostos: impostosDoItemEntradaSchema.prefault({}),
  /**
   * Como este material se divide entre obras. Pode ser vazio.
   *
   * Vazio significa "não se divide": o destino é o depósito do item, e o consumo
   * dirá depois para qual obra foi. Exigir um rateio de uma linha em toda compra
   * de almoxarifado seria burocracia sobre o caso mais comum.
   */
  rateios: rateiosSchema,
  observacao: z.string().trim().max(500, 'A observação tem no máximo 500 caracteres.').optional(),
});

export type ItemPedidoCompraEntrada = z.infer<typeof itemPedidoCompraEntradaSchema>;

/**
 * O rateio, quando existe, distribui **exatamente** a quantidade pedida.
 *
 * Rateio que soma menos deixa material sem destino; rateio que soma mais
 * distribui o que não se comprou. É a mesma exigência de `.spec/010` para o
 * rateio financeiro ("a soma dos valores rateados deve ser sempre exatamente
 * igual ao valor do documento"), aplicada à quantidade porque aqui o que se
 * divide é material.
 *
 * A comparação é sobre inteiros escalados, e não sobre `Number`: uma diferença
 * de 0,0001 entre dez parcelas empataria em ponto flutuante e passaria.
 */
function validarRateiosDosItens(itens: ItemPedidoCompraEntrada[], ctx: z.RefinementCtx): void {
  itens.forEach((item, indice) => {
    if (item.rateios.length === 0) return;

    const distribuido = somarQuantidade(item.rateios.map((rateio) => rateio.quantidade));
    if (compararQuantidade(distribuido, item.quantidadePedida) === 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'rateios'],
      message: `O rateio distribui ${distribuido} e o item pede ${item.quantidadePedida}. As duas quantidades têm de bater.`,
    });
  });
}

/**
 * O mesmo saldo de solicitação, ou a mesma proposta, em duas linhas é sempre
 * digitação repetida.
 *
 * **Não há recusa por insumo × depósito**, ao contrário da solicitação, e é
 * decisão: comprar o mesmo material em dois lotes de preços diferentes no mesmo
 * pedido é corriqueiro — o fornecedor tem 60 sacos do lote antigo e 40 do novo.
 * Recusar pelo par insumo × depósito obrigaria a abrir dois pedidos para o mesmo
 * fornecedor na mesma negociação.
 *
 * O que **é** duplicação é apontar duas linhas para o mesmo saldo: as duas
 * somariam `quantidade_atendida` no mesmo item de solicitação, e a política de
 * excedente avaliaria cada uma isoladamente contra o total.
 */
function validarOrigensDistintas(itens: ItemPedidoCompraEntrada[], ctx: z.RefinementCtx): void {
  acusarOrigemRepetida(
    itens,
    'solicitacaoCompraItemId',
    'Este item de solicitação já está sendo atendido em outra linha. Some as quantidades numa linha só.',
    ctx,
  );
  acusarOrigemRepetida(
    itens,
    'cotacaoPropostaId',
    'Esta proposta de cotação já está sendo consumida em outra linha.',
    ctx,
  );
  acusarOrigemRepetida(
    itens,
    'listaCompraDiretaItemId',
    'Este material de lista de compra direta já está em outra linha do pedido.',
    ctx,
  );
}

/**
 * Uma origem repetida entre as linhas, apontando a segunda.
 *
 * Uma função para as três, e não três blocos iguais: a terceira origem chegou
 * com a lista de compra direta, e escrever o mesmo laço pela terceira vez era o
 * caminho para a próxima nascer sem a conferência. O campo entra como chave
 * tipada — um nome errado não compila.
 */
function acusarOrigemRepetida(
  itens: ItemPedidoCompraEntrada[],
  campo: 'solicitacaoCompraItemId' | 'cotacaoPropostaId' | 'listaCompraDiretaItemId',
  mensagem: string,
  ctx: z.RefinementCtx,
): void {
  const vistos = new Set<string>();

  itens.forEach((item, indice) => {
    const origem = item[campo];
    if (origem === null) return;

    if (vistos.has(origem)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [indice, campo], message: mensagem });
    }
    vistos.add(origem);
  });
}

const itensSchema = z
  .array(itemPedidoCompraEntradaSchema)
  .min(1, 'O pedido precisa de pelo menos um item.')
  .max(MAXIMO_ITENS_PEDIDO, `O pedido tem no máximo ${MAXIMO_ITENS_PEDIDO} itens.`)
  .superRefine(validarOrigensDistintas)
  .superRefine(validarRateiosDosItens);

/** Item como a API o devolve, com os nomes resolvidos e os valores prontos. */
export const itemPedidoCompraSchema = z.object({
  id: uuidSchema,
  /** O vínculo gravado. Vem na leitura porque a nota de compra vai apontar para ele. */
  insumoEmpresaId: uuidSchema,
  /** O insumo por trás do vínculo — é ele que o formulário devolve no `PUT`. */
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  /**
   * Material ou serviço — é o que decide **quais impostos** a linha pergunta.
   *
   * Por item, e não pelo documento como na cotação: o mesmo pedido compra
   * cimento e contrata a bomba de concretagem, e as duas linhas destacam
   * tributos diferentes. `blocosDeImposto` recebe este valor.
   *
   * Desnormalizado na leitura, e não buscado pela tela: o formulário abriria uma
   * consulta por item só para saber quais campos desenhar, e num pedido de vinte
   * linhas isso são vinte idas ao servidor para uma decisão que o `SELECT` dos
   * itens já podia ter respondido — ele junta `insumos` de qualquer forma, para
   * o nome.
   */
  insumoTipo: tipoInsumoSchema,
  /**
   * A conta contábil **do insumo**, desnormalizada na leitura.
   *
   * O pedido não a usa para nada: quem classifica é a nota, e o pedido não gera
   * lançamento contábil. Ela viaja aqui para a **entrada de nota por pedido**,
   * que monta o rascunho a partir destes itens e precisa levar a classificação
   * de cada linha junto.
   *
   * Desnormalizada, e não buscada pela tela: a entrada pode escolher vários
   * pedidos de uma vez, e cada linha é de um insumo diferente — resolver isso no
   * cliente seria uma consulta por item, ou uma busca paginada que cortaria os
   * insumos além do ducentésimo em silêncio.
   *
   * Nula quando o insumo não está classificado, e nesse caso a linha da nota
   * nasce sem conta — o mesmo estado do item criado à mão.
   */
  insumoContaContabilId: uuidSchema.nullable(),
  unidadeSigla: z.string().nullable(),
  /**
   * Casas do preço daquele insumo, desnormalizadas como a sigla e pelo mesmo
   * motivo: quem mostra a linha precisa saber a escala sem uma consulta por item.
   *
   * A listagem resume cada linha como "5.000 L × 6,123456 = 30.617,28", e com
   * duas casas fixas o unitário do combustível apareceria arredondado ao lado de
   * um total que não bate com ele.
   */
  casasDecimaisPreco: z.number().int(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  solicitacaoCompraItemId: uuidSchema.nullable(),
  /** A solicitação daquele item, resolvida — é ela que a tela sabe abrir. */
  solicitacaoCompraId: uuidSchema.nullable(),
  cotacaoPropostaId: uuidSchema.nullable(),
  /** A cotação da proposta, resolvida, pelo mesmo motivo. */
  cotacaoPrecoId: uuidSchema.nullable(),
  fornecedorDaPropostaNome: z.string().nullable(),
  /** O item de lista de compra direta que gerou a linha, e a lista dele. */
  listaCompraDiretaItemId: uuidSchema.nullable(),
  /** Resolvidos pelo mesmo motivo da solicitação: é o que a tela sabe abrir. */
  listaCompraDiretaId: uuidSchema.nullable(),
  listaCompraDiretaNumero: z.number().int().nullable(),
  /** Strings decimais, nunca number: ponto flutuante perde casas em silêncio. */
  quantidadePedida: quantidadeSchema,
  /** Escrita pela nota de compra, ao receber a mercadoria. */
  quantidadeAtendida: quantidadeSchema,
  /** O saldo que o fornecedor não vai entregar. Escrita pelo encerramento. */
  quantidadeBaixada: quantidadeSchema,
  /** `pedida − atendida − baixada`, calculado pelo servidor. */
  quantidadeEmAberto: quantidadeSchema,
  /** Seis casas: o preço é do fornecedor. O produto dele é que é dinheiro. */
  valorUnitario: precoUnitarioSchema,
  valorInsumo: dinheiroSchema,
  valorOutros: dinheiroSchema,
  /** Cobrado pelo fornecedor no `CIF`. Zero nas outras modalidades. */
  valorFrete: dinheiroSchema,
  valorDesconto: dinheiroSchema,
  valorTotal: dinheiroSchema,
  valorCustoEstoque: dinheiroSchema,
  impostos: impostosDoItemSchema,
  rateios: z.array(rateioDoItemSchema),
  observacao: z.string().nullable(),
});

export type ItemPedidoCompra = z.infer<typeof itemPedidoCompraSchema>;

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Payload de gravação.
 *
 * **Não tem `situacao` nem `motivoEncerramento`.** A situação é derivada do
 * atendimento dos itens, como na solicitação; encerrar é ação própria, com rota
 * própria, porque exige verificar o atendimento antes de decidir se o caso é
 * cancelar ou baixar.
 *
 * **Não tem obra.** A obra entra pelo **rateio do item**, e não pelo cabeçalho,
 * e é a diferença central em relação à solicitação: um pedido a um fornecedor
 * costuma abastecer mais de uma obra no mesmo carregamento, e uma obra no
 * cabeçalho obrigaria a abrir um pedido por obra — perdendo justamente a
 * negociação de volume que motiva o documento.
 */
const pedidoCompraBaseSchema = z.object({
  /** Empresa do grupo — `pessoas` com `is_empresa`. É a dona dos depósitos. */
  empresaId: uuidSchema,
  /** De quem se compra. `pessoas` com `is_fornecedor`. */
  fornecedorId: uuidSchema,
  /** Em quantas vezes se paga. Faz parte do preço acordado. */
  condicaoPagamentoId: uuidSchema,
  /** Dia do pedido. Data de negócio, sem hora. */
  dataLancamento: dataDoFatoSchema,
  /**
   * Quem paga o transporte. **A modalidade fica aqui; o valor, no item.**
   *
   * A modalidade é do documento porque o pedido é de um fornecedor só e uma
   * negociação só: "quem entrega" não muda de linha para linha. O valor desceu
   * para o item — ver `valorFrete` em `itemPedidoCompraEntradaSchema`.
   */
  frete: fretePedidoSchema.prefault('SEM'),
  itens: itensSchema,
  observacao: z.string().trim().max(1000, 'A observação tem no máximo 1000 caracteres.').optional(),
});

/**
 * Valor de frete só existe no `CIF`.
 *
 * `superRefine` sobre o **documento**, e não `refine` no campo do item, porque a
 * recusa é sobre o par modalidade × valor — e os dois moram em níveis
 * diferentes: a modalidade no cabeçalho, o valor em cada linha. É também por
 * isso que a regra não pode ser um `CHECK` do banco, como era enquanto o valor
 * ficava no cabeçalho: nenhum `CHECK` alcança a tabela do pai a partir do filho.
 *
 * A mensagem chega presa ao campo da **linha**, e não ao documento: é ela que o
 * comprador limpa. A outra saída — trocar a modalidade para CIF — está escrita
 * na frase, porque quem digitou o valor provavelmente errou foi a modalidade.
 */
function validarFreteDosItens(
  pedido: { frete: FretePedido; itens: readonly { valorFrete: string }[] },
  ctx: z.RefinementCtx,
): void {
  if (freteTemValor(pedido.frete)) return;

  pedido.itens.forEach((item, indice) => {
    if (Number(item.valorFrete) === 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['itens', indice, 'valorFrete'],
      message:
        'Só o frete CIF tem valor a informar: nele o fornecedor entrega e cobra. Zere o valor, ou mude a modalidade do pedido para CIF.',
    });
  });
}

/**
 * O payload, com a regra do frete aplicada.
 *
 * A base fica privada e só este nome sai do módulo: dois schemas exportados —
 * um com a regra e outro sem — seriam um convite a importar o errado, e o
 * caminho que escapasse da validação seria justamente o de gravação.
 */
export const criarPedidoCompraSchema = pedidoCompraBaseSchema.superRefine(validarFreteDosItens);

export type CriarPedidoCompra = z.infer<typeof pedidoCompraBaseSchema>;
export const atualizarPedidoCompraSchema = criarPedidoCompraSchema;
export type AtualizarPedidoCompra = z.infer<typeof atualizarPedidoCompraSchema>;

/**
 * Pedido como a API o devolve.
 *
 * Os nomes de empresa, fornecedor e condição vêm **desnormalizados**: a listagem
 * mostra "Casa do Construtor · 30/60/90" e não dois uuid, e resolver isso no
 * cliente significaria duas consultas por linha.
 */
export const pedidoCompraSchema = z.object({
  id: uuidSchema,
  /**
   * O número sequencial do documento, **do servidor**.
   *
   * Não está no payload de gravação: quem o atribui é o contador da conta, na
   * transação que cria o pedido. É o número que vai ao fornecedor e volta
   * escrito na nota, e é único na conta inteira — não por empresa do grupo.
   *
   * Sequência própria, separada da solicitação: numeração compartilhada faria o
   * pedido 431 vir depois da solicitação 430 sem que os dois tivessem relação.
   */
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  fornecedorId: uuidSchema,
  /** Nome de uso: fantasia quando há, razão social quando não (`nomeDeUso`). */
  fornecedorNome: z.string().nullable(),
  condicaoPagamentoId: uuidSchema,
  /** `descricao` no cadastro — o nome do campo acompanha a tabela de origem. */
  condicaoPagamentoDescricao: z.string().nullable(),
  dataLancamento: dataSchema,
  /** A modalidade. O valor cobrado está em cada item — ver `itemPedidoCompraSchema`. */
  frete: fretePedidoSchema,
  situacao: situacaoDocumentoSchema,
  motivoEncerramento: z.string().nullable(),
  /** Da autorização (`aprovacao-compra.ts`, decisão 108). */
  situacaoAprovacao: situacaoAprovacaoSchema,
  /**
   * Quando e por quem o pedido foi cancelado ou baixado. Nulos no pedido
   * aberto. São o que a marca d'água do impresso cancelado mostra.
   */
  encerradoEm: dataHoraSchema.nullable(),
  encerradoPorNome: z.string().nullable(),
  observacao: z.string().nullable(),
  /**
   * A soma dos totais dos itens, **gravada** no documento.
   *
   * Gravada, e não somada na leitura, pelo mesmo motivo dos valores do item: o
   * pedido é um fato daquela data, e um total recalculado meses depois mudaria
   * junto com qualquer correção futura da fórmula (ADR 0004).
   */
  valorTotal: dinheiroSchema,
  /**
   * Os itens vêm **na listagem também**, não só no documento aberto.
   *
   * É o que distingue dois pedidos ao mesmo fornecedor no mesmo dia, e é o que a
   * coluna "Itens" resume. O custo é uma segunda consulta por página, não uma
   * por linha — a mesma escolha da solicitação e da cotação.
   */
  itens: z.array(itemPedidoCompraSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type PedidoCompra = z.infer<typeof pedidoCompraSchema>;

export const listaPedidosCompraSchema = respostaListaSchema(pedidoCompraSchema);
export type ListaPedidosCompra = z.infer<typeof listaPedidosCompraSchema>;

/* -------------------------------------------------------------------------- */
/* Aviso da gravação                                                          */
/* -------------------------------------------------------------------------- */

/**
 * O que a gravação aceitou **com ressalva**.
 *
 * Existe por causa de uma política só: `ALERTAR`, na configuração da conta.
 * `.spec/010` a define como "aceita e devolve um aviso", e sem um canal de aviso
 * a metade "devolve" não tinha para onde ir — a avaliação do domínio produzia
 * `avisos` e o repository descartava o resultado de sucesso inteiro, então a
 * política se comportava como se não houvesse conferência nenhuma.
 *
 * **Aviso não é erro.** O erro recusa a gravação e chega como `ErroHttp`, com
 * `code` e status; o aviso chega **junto com o documento gravado**, num 201 ou
 * 200, e diz o que passou e por quanto. Misturar os dois obrigaria a tela a
 * distinguir "falhou" de "gravou, mas veja isto" pelo texto da mensagem.
 *
 * Os três números vão todos, e não só o excedente: quem confere contra o papel
 * lê "pediu 120, a obra solicitou 100" — o excedente sozinho obrigaria a refazer
 * a conta para saber se está certo. Ver `mensagemDoAviso`.
 */
export const avisoDoPedidoSchema = z.object({
  /**
   * Um código só, hoje.
   *
   * `z.literal`, e não um enum de um elemento: no dia em que houver o segundo
   * aviso, trocar o literal por um enum é a mudança que o compilador cobra em
   * todo lugar que trata o código — e é isso que se quer.
   */
  code: z.literal('quantidade_excede_solicitado'),
  /** A linha do pedido. Sem ela, quem tem quinze itens confere um a um. */
  indice: z.number().int().min(0),
  /** O que a obra pediu no item de solicitação de origem. */
  solicitada: quantidadeSchema,
  /** O total pedido contra aquele item, somando os outros pedidos. */
  pedida: quantidadeSchema,
  /** `pedida − solicitada`. Sempre maior que zero: sem excesso não há aviso. */
  excedente: quantidadeSchema,
});

export type AvisoDoPedido = z.infer<typeof avisoDoPedidoSchema>;

/**
 * O que o `POST` e o `PUT` devolvem: o documento **e** as ressalvas.
 *
 * O corpo deixou de ser o pedido nu quando `ALERTAR` passou a avisar de verdade.
 * A alternativa — devolver os avisos num cabeçalho HTTP — foi descartada: o
 * cliente teria de lê-los fora do corpo tipado, e nada garantiria que o schema e
 * o cabeçalho não divergissem na primeira correção.
 *
 * O `GET` continua devolvendo o pedido nu: ler um documento não é um ato que se
 * possa ressalvar, e o aviso é sobre o que **esta gravação** fez.
 */
export const pedidoGravadoSchema = z.object({
  pedido: pedidoCompraSchema,
  /** Vazia na gravação sem ressalva, que é o caso normal. */
  avisos: z.array(avisoDoPedidoSchema),
});

export type PedidoGravado = z.infer<typeof pedidoGravadoSchema>;

/**
 * O texto de um aviso, com os números crus.
 *
 * Vive no contrato porque as duas pontas precisam do mesmo texto, e é o mesmo
 * arranjo de `erroDaSomaDasEscolhidas` na cotação: o contrato não conhece a
 * sigla da unidade, e quem quiser escrever "20 SC" formata por cima. O que não
 * se duplica é o que o aviso **diz**.
 *
 * A segunda frase existe porque a primeira sozinha soa como defeito. O comprador
 * precisa saber que o excedente foi aceito de propósito e que a solicitação não
 * vai aparecer atendida acima de 100% — quem trava isso é o `LEAST` de
 * `recalcularAtendimentoDasSolicitacoes`.
 */
export function mensagemDoAviso(aviso: AvisoDoPedido): string {
  return `Item ${aviso.indice + 1}: pedido de ${aviso.pedida} contra ${aviso.solicitada} solicitados — ${aviso.excedente} acima. A conta aceita o excedente com aviso, e o atendimento da solicitação para no que foi solicitado.`;
}

/**
 * Todas as ressalvas num texto só.
 *
 * Uma faixa por item transformaria um pedido de dez linhas excedentes em dez
 * faixas empilhadas, e a tela ensinaria a fechar todas sem ler. `null` quando
 * não há aviso — é o que deixa quem chama escrever `?? confirmacao`.
 */
export function mensagemDosAvisos(avisos: readonly AvisoDoPedido[]): string | null {
  if (avisos.length === 0) return null;

  return avisos.map(mensagemDoAviso).join(' ');
}

/* -------------------------------------------------------------------------- */
/* Encerramento                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Cancelar ou baixar, sempre com motivo.
 *
 * A mesma forma da solicitação, e pelo mesmo motivo: qual das duas é permitida
 * depende do atendimento dos itens, que é a verificação que o servidor faz nos
 * dois casos. Duas rotas repetiriam a verificação e divergiriam na primeira
 * correção.
 *
 * Encerrar o pedido **devolve o saldo à solicitação**: o que o fornecedor não
 * vai entregar volta a ser saldo em aberto lá, e pode ser comprado de outro. É a
 * diferença mais importante em relação ao encerramento da solicitação, que não
 * devolve nada a lugar nenhum.
 */
export const encerrarPedidoCompraSchema = z.object({
  destino: z.enum(['CANCELADO', 'BAIXADO']),
  motivo: z
    .string()
    .trim()
    .min(5, 'Escreva o motivo do encerramento.')
    .max(500, 'O motivo tem no máximo 500 caracteres.'),
});

export type EncerrarPedidoCompra = z.infer<typeof encerrarPedidoCompraSchema>;

/* -------------------------------------------------------------------------- */
/* Listagem                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: documento não se inativa, e a coluna equivalente é `situacao`. O
 * recorte natural é **situação, fornecedor e período** — "o que está pendente de
 * entrega" é a pergunta que a tela abre respondendo.
 *
 * O filtro de **obra** alcança o rateio do item, e não o cabeçalho: a obra não
 * está no documento, e "o que se comprou para a Residencial Aurora" é a pergunta
 * que o engenheiro faz.
 */
export const listarPedidosCompraQuerySchema = paginacaoSchema.omit({ q: true }).extend({
  /**
   * O número do documento, exato.
   *
   * É como se chega ao pedido quando o fornecedor liga citando o número dele.
   * Exato, e não "contém": o número é curto, e "43" traria 43, 143, 430 e 4300.
   */
  numero: z.coerce.number().int().min(1).optional(),
  empresaId: uuidSchema.optional(),
  /**
   * O material comprado, por id.
   *
   * Alcança os **itens**, e não o cabeçalho: o pedido não tem insumo, cada linha
   * dele tem. É `EXISTS` no servidor, não junção — a junção traria o pedido
   * repetido uma vez por item e a paginação passaria a contar itens.
   *
   * Substituiu a busca livre, que varria observação e nome do fornecedor. As
   * duas metades envelheceram mal: o fornecedor já tem filtro próprio ao lado, e
   * a observação é anotação de quem digitou.
   */
  insumoId: uuidSchema.optional(),
  fornecedorId: uuidSchema.optional(),
  situacao: situacaoDocumentoSchema.optional(),
  /**
   * A fila do autorizador: `AGUARDANDO` traz o que espera decisão (decisão
   * 108). Igualdade sobre a coluna, como a situação.
   */
  situacaoAprovacao: situacaoAprovacaoSchema.optional(),
  /** Recorta pelos rateios dos itens. */
  obraId: uuidSchema.optional(),
  /** Recorta pelos itens: traz o pedido que consome saldo desta solicitação. */
  solicitacaoCompraId: uuidSchema.optional(),
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarPedidosCompraQuery = z.infer<typeof listarPedidosCompraQuerySchema>;

export const COLUNAS_ORDENAVEIS_PEDIDO_COMPRA = [
  'numero',
  'dataLancamento',
  'situacao',
  'valorTotal',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas entre a tela e a API                                 */
/* -------------------------------------------------------------------------- */

/**
 * O pedido ainda se altera?
 *
 * Vive no contrato, e não no formulário nem no service, porque os dois precisam
 * da **mesma** regra: a tela decide se os campos são editáveis, e a API decide
 * se aceita o `PUT`. Duas cópias divergiriam na primeira correção, e a
 * divergência apareceria como formulário que deixa salvar e servidor que recusa.
 */
export function pedidoEditavel(situacao: SituacaoPedidoCompra): boolean {
  return situacao === 'PENDENTE';
}

/**
 * Qual encerramento cabe, dado o atendimento dos itens.
 *
 * Cancelar exige atendimento zero em **todos** os itens; com entrega parcial, o
 * caso é baixar. A regra é a mesma da solicitação (`.spec/001`, seção 2.2 (e)) e
 * está implementada em `validarEncerramento`, em `@construtora/domain`. Esta
 * função não a duplica: ela responde a pergunta que a *tela* faz — qual botão
 * oferecer — sobre os dados que a tela já tem.
 */
export function encerramentoDoPedidoPermitido(
  itens: readonly { quantidadeAtendida: string }[],
): 'CANCELADO' | 'BAIXADO' {
  const temAtendimento = itens.some((item) => Number(item.quantidadeAtendida) > 0);
  return temAtendimento ? 'BAIXADO' : 'CANCELADO';
}

/**
 * O total do pedido: a soma dos totais dos itens, e nada além disso.
 *
 * Existe para a tela mostrar o número enquanto o comprador digita. O servidor
 * grava o resultado da mesma função sobre os mesmos valores — é por isso que ela
 * vive aqui, e não em cada ponta.
 *
 * **O frete não é mais parcela do documento.** Ele descia do cabeçalho e era
 * somado aqui, à parte dos itens; agora está dentro do `valorTotal` de cada
 * linha, e somá-lo de novo contaria o transporte duas vezes.
 */
export function totalDoPedido(itens: readonly { valorTotal: string }[]): string {
  return somarDinheiro(itens.map((item) => item.valorTotal));
}

/* -------------------------------------------------------------------------- */
/* Distribuição de um total entre os itens                                    */
/* -------------------------------------------------------------------------- */

/**
 * Reparte um valor único entre os itens, na proporção do que cada um vale.
 *
 * É o que está por trás do botão que informa "frete total", "outros total" e
 * "desconto total" de uma vez. O fornecedor manda o documento com um número só
 * no rodapé — R$ 480 de frete do carregamento inteiro —, e o pedido precisa
 * daquilo por linha, porque é por linha que o custo do estoque se forma.
 *
 * **A base é o valor do insumo**, quantidade × unitário, e não o total do item.
 * O total já contém frete, outros e desconto — que são justamente os números
 * sendo distribuídos —, então usá-lo como base tornaria o resultado dependente
 * da ordem em que os três fossem informados. A mercadoria pura não se move.
 *
 * **A sobra de centavos vai para a última linha.** Repartir R$ 100 entre três
 * itens iguais dá 33,33 três vezes e sobra um centavo; distribuí-lo "para quem
 * tiver a maior fração" seria mais justo e menos previsível — quem confere a
 * conta refaz a divisão e não acha o critério. Com a sobra no fim, a soma fecha
 * sempre e o desvio é de um centavo numa linha que a pessoa consegue apontar.
 *
 * **Base zerada devolve tudo zerado**, e não uma divisão por zero: itens sem
 * quantidade ou sem preço ainda não formam proporção nenhuma, e o comprador
 * está no meio da digitação.
 */
export function distribuirPorValorDoInsumo(
  total: string,
  /** O valor de mercadoria de cada item, na ordem da lista. */
  valoresDoInsumo: readonly string[],
): string[] {
  const alvo = escalar(total, CASAS_DINHEIRO);
  const bases = valoresDoInsumo.map((valor) => escalar(valor, CASAS_DINHEIRO));
  const soma = bases.reduce((acumulado, base) => acumulado + base, 0n);

  if (soma === 0n || bases.length === 0) return bases.map(() => '0.00');

  const partes = bases.map((base) => dividirArredondando(alvo * base, soma));
  const distribuido = partes.reduce((acumulado, parte) => acumulado + parte, 0n);

  // A última linha recebe o que faltou — ou devolve o que sobrou, quando o
  // arredondamento de cada parte para cima passou do alvo.
  const ultimo = partes.length - 1;
  partes[ultimo] = (partes[ultimo] ?? 0n) + (alvo - distribuido);

  return partes.map((parte) => desescalar(parte, CASAS_DINHEIRO));
}

/** O saldo do item que ainda não chegou nem foi baixado. */
export function saldoEmAbertoDoItem(item: {
  quantidadePedida: string;
  quantidadeAtendida: string;
  quantidadeBaixada: string;
}): string {
  return subtrairQuantidade(
    subtrairQuantidade(item.quantidadePedida, item.quantidadeAtendida),
    item.quantidadeBaixada,
  );
}

/**
 * Resumo dos itens, para a listagem: `3 itens · 2 em aberto`.
 *
 * Vive no contrato pelo mesmo motivo que `resumoDeItens` da solicitação: a
 * listagem e o seletor de pedido da nota de compra mostram a mesma coisa, e duas
 * implementações escreveriam "0 em aberto" e "tudo entregue" para o mesmo
 * documento.
 */
export function resumoDeItensDoPedido(itens: readonly ItemPedidoCompra[]): string {
  if (itens.length === 0) return '—';

  const plural = itens.length === 1 ? 'item' : 'itens';
  const emAberto = itens.filter((item) => Number(item.quantidadeEmAberto) > 0).length;

  if (emAberto === 0) return `${itens.length} ${plural} · sem saldo`;
  return `${itens.length} ${plural} · ${emAberto} em aberto`;
}

/**
 * As obras que este pedido abastece, sem repetir, na ordem em que aparecem.
 *
 * **O pedido não tem obra**: ela entra pelo rateio de cada item, e um pedido a um
 * fornecedor costuma abastecer mais de uma obra no mesmo carregamento — é a razão
 * de o cabeçalho não ter o campo (ver `pedidoCompraBaseSchema`). Por isso a
 * resposta é uma **lista**, e não um nome: quem chama decide se mostra o primeiro
 * com "+2 obras", se mostra todos, ou se diz "3 obras".
 *
 * Vazia quando nenhum item foi rateado, e isso não é falta de dado: sem rateio o
 * material fica no depósito do item, e é o consumo que dirá para qual obra foi.
 * Quem exibe precisa dizer isso em vez de mostrar um traço mudo.
 *
 * A ordem é a dos itens, e não alfabética: a primeira obra da lista é a do
 * primeiro item, que é o que a linha da listagem mostra em pé — e alfabetizar
 * faria o nome exibido mudar quando o comprador reordenasse os itens.
 *
 * Vive no contrato pelo mesmo motivo que `resumoDeItensDoPedido`: a listagem de
 * pedidos e a tela de geração leem a mesma coisa, e duas implementações
 * discordariam no dia em que uma passasse a contar obra repetida.
 */
export function obrasDoPedido(
  itens: readonly { rateios: readonly { obraNome: string | null }[] }[],
): string[] {
  const vistas = new Set<string>();

  for (const item of itens) {
    for (const rateio of item.rateios) {
      if (rateio.obraNome !== null) vistas.add(rateio.obraNome);
    }
  }

  return [...vistas];
}
